import { PropsWithChildren } from 'react';
import { NavLink } from 'react-router-dom';

import { ChevronRight16 } from '@ingrid/iconography/react';
import { NavigationList, PageTitle, PageWrapper, PileBadge } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';
import { usePermissions } from '~src/main/hooks/usePermissions';
import { TransportRole } from '~src/main/providers/PermissionProvider';
import { useGetUserQuery } from '~src/main/queries';
import { AppRoute } from '~src/main/routes';

import { useSafePendingShipmentCountQuery } from './pending-shipment/queries';

type ItemProps = PropsWithChildren & {
  roles: TransportRole[];
  href: string;
};

function Item({ href, roles, children, ...rest }: ItemProps) {
  const { hasRole } = usePermissions();

  if (!hasRole(roles)) {
    return false;
  }

  return (
    <NavigationList.Item as={NavLink} to={href} {...rest}>
      <NavigationList.Text>{children}</NavigationList.Text>
      <NavigationList.Icon>
        <ChevronRight16 />
      </NavigationList.Icon>
    </NavigationList.Item>
  );
}

function PendingShipmentCount() {
  const { data, isError, isLoading } = useSafePendingShipmentCountQuery();

  if (isLoading || isError) {
    return false;
  }

  return <PileBadge variant="critical">{data?.total_count ?? 0}</PileBadge>;
}

export function Page() {
  const { data } = useGetUserQuery();
  const { t } = useTranslation();

  return (
    <PageWrapper variant="center" gap="lg">
      <PageTitle.Title>{t('common.settings')}</PageTitle.Title>

      <NavigationList.List aria-label={t('common.settings')} style={{ width: '100%' }}>
        <Item href={AppRoute.CARRIER_CONTRACTS} roles={['transport:view']}>
          {t('common.carrierContracts')}
        </Item>
        <Item href={AppRoute.ADDRESS_BOOK} roles={['transport:view']}>
          {t('common.addressBook')}
        </Item>
        <Item href={AppRoute.SHIPMENT_TEMPLATES} roles={['transport:view']}>
          {t('common.shipmentTemplates')}
        </Item>
        <Item href={AppRoute.BOOKING_RULES} roles={['transport:view']}>
          {t('common.bookingRules')}
        </Item>
        {data?.super_user && (
          <Item href={AppRoute.BOOKING_RULE_CONFIGURATOR} roles={['transport:view', 'transport:edit']}>
            {t('common.bookingRuleConfigurator')} <PileBadge variant="critical">Beta</PileBadge>
          </Item>
        )}
        <Item href={AppRoute.PENDING_SHIPMENTS} roles={['transport:view']}>
          {t('common.pendingShipments')} <PendingShipmentCount />
        </Item>
      </NavigationList.List>
    </PageWrapper>
  );
}
